import { IController, IScope, IAttributes, IInterpolateService, IRootElementService } from 'angular';
import { DataTypes } from './enumerations';
export function toCamelCase(value: string) {
	return value.toLowerCase().replace(/-([a-z])/g, (g) => g[1].toUpperCase());
}

const filterExpressionPathPropertiesNumber = 4;
export interface IFilterExpressionPath {
	widgetId: string;
	viewName: string;
	controlName: string;
	propertyName: string;
}
function isPathCorrect(path?: Partial<IFilterExpressionPath>): path is IFilterExpressionPath {
	if (!path) {
		return false;
	}
	const values = Object.values(path);
	return values.length === filterExpressionPathPropertiesNumber && values.every((pathValue) => typeof pathValue === 'string' && pathValue.length > 0);
}

interface IServiceContext {
	info: {
		widgetId: string;
		viewName: string;
	};
}

export interface ScopeWithServiceContext extends IScope {
	mxServiceContextController?: IServiceContext;
}

export interface FilterExpressionProps {
	propertyName: string;
	controlMxAtId: string;
	controller: IController;
	$scope: ScopeWithServiceContext;
	$attrs: IAttributes;
	$interpolate: IInterpolateService;
	$element: IRootElementService;
	onChanged?: (value: FilterExpression) => void;
}

export type IEmptyFilterExpressionRequest = Record<string, never>;

export interface IFilterExpressionWithID {
	Id: string;
	Params: IStrictParam[];
}

export interface IFilterExpressionWithPath {
	Path: IFilterExpressionPath;
	Params: IStrictParam[];
}
export interface IFilterExpressionRequest {
	FilterExpression: IFilterExpressionWithID | IFilterExpressionWithPath;
}

export interface IWhereExpressionRequest {
	FilterExpression?: {
		Path: IFilterExpressionPath;
	};
	WhereExpression: string;
}

export type FilterExpressionRequestParams = IFilterExpressionRequest | IWhereExpressionRequest | IEmptyFilterExpressionRequest;

export interface IFilterExpression {
	toRequest(): FilterExpressionRequestParams;
}

export class FilterExpression implements IFilterExpression {
	private filterDefinition?: IFilterDefinition;
	private id?: string;
	private path?: IFilterExpressionPath;
	private expression?: string;

	constructor(private props: FilterExpressionProps) {
		const { $attrs, propertyName } = this.props;

		const propertyNameCamelCase = toCamelCase(propertyName);
		const attrValue = $attrs[propertyNameCamelCase];
		const idValue = $attrs[`${propertyNameCamelCase}Id`];

		if (typeof idValue === 'string' && idValue.length > 0) {
			this.initComponentFormat(idValue);
		} else if (typeof attrValue === 'string' && attrValue.length > 0) {
			this.initLayoutTransformationFormat(attrValue);
		}
	}

	private getServiceContext(): IServiceContext | undefined {
		let scope = this.props.$scope;

		while (!scope.mxServiceContextController && scope.$parent) {
			scope = scope.$parent;
		}

		return scope.mxServiceContextController;
	}

	private getPath() {
		const { propertyName, controlMxAtId } = this.props;

		const path: Partial<IFilterExpressionPath> = {
			propertyName,
			controlName: controlMxAtId,
		};

		const serviceContext = this.getServiceContext();
		if (serviceContext?.info) {
			path.widgetId = serviceContext.info.widgetId;
			path.viewName = serviceContext.info.viewName;
		}

		return path;
	}

	private initComponentFormat(idValue: string): void {
		const { $attrs, onChanged, propertyName } = this.props;

		this.id = idValue;

		const filterDefinition: IStrictFilterDefinition = {
			IsStrict: true,
			StrictExpression: {
				Params: [],
			},
		};

		const paramsAttr = $attrs[toCamelCase(`${propertyName}-params`)];
		if (paramsAttr) {
			filterDefinition.StrictExpression.Params = this.initScopeValue<IStrictParam[]>(paramsAttr, (newValue) => {
				filterDefinition.StrictExpression.Params = newValue;
				onChanged?.(this);
			});
		}

		this.filterDefinition = filterDefinition;
	}

	private initLayoutTransformationFormat(attrValue: string): void {
		const { $scope, onChanged } = this.props;

		let newFormat = false;
		let value;
		try {
			value = JSON.parse(attrValue);
			newFormat = typeof value === 'object' && value && (typeof value.IsStrict === 'boolean' || typeof value.LegacyExpression === 'string');
		} catch (e) {
			//nothing
		}
		if (!newFormat) {
			this.initDeprecatedFilterFormat();
			return;
		}
		this.filterDefinition = $scope.$parent.$eval(attrValue);

		const path = this.getPath();
		if (isPathCorrect(path)) {
			this.path = path;
		} else if (this.filterDefinition?.IsStrict) {
			console.error('Filter is used with wrong Path and without Id. Filter will be ignored', {
				path: path,
				filter: attrValue,
			});
		}

		if (this.filterDefinition?.IsStrict) {
			this.filterDefinition?.StrictExpression?.Params?.forEach((param) => {
				param.Value = this.initScopeValue(param.Value, (newValue) => {
					param.Value = newValue;
					onChanged?.(this);
				});
			});
		} else {
			this.expression = this.initScopeValue(this.filterDefinition?.LegacyExpression || '', (newValue) => {
				this.expression = newValue;
				onChanged?.(this);
			});
		}
	}

	private initDeprecatedFilterFormat() {
		const { propertyName, $attrs, $element, onChanged, $scope } = this.props;
		const propertyNameCamelCase = toCamelCase(propertyName);

		/*console.error('Filter is used in deprecated format which will be removed in next versions.', {
			control: path.controlName,
			property: path.propertyName,
			filter: $attrs[propertyNameCamelCase],
		});*/

		const rawAttrValue = $element.attr(propertyName);
		if (typeof rawAttrValue !== 'string') {
			return;
		}

		const initialAttrValue = $attrs[propertyNameCamelCase];

		let isInterpolation = rawAttrValue.startsWith('{{');
		if (!isInterpolation) {
			try {
				$scope.$parent.$eval(initialAttrValue);
				isInterpolation = false;
			} catch (e) {
				isInterpolation = true;
			}
		}

		if (isInterpolation) {
			this.expression = this.initInterpolatedValue(propertyNameCamelCase, (newValue) => {
				this.expression = newValue;
				onChanged?.(this);
			});
		} else {
			this.expression = this.initScopeValue(initialAttrValue || '', (newValue) => {
				this.expression = newValue;
				onChanged?.(this);
			});
		}
	}

	private initInterpolatedValue<T = unknown>(attrName: string, onChanged: (newValue: T) => void) {
		const { $scope, $attrs } = this.props;

		let value = $attrs[attrName];

		const unsubscribe = $attrs.$observe(attrName, (interpolatedValue?: string) => {
			value = interpolatedValue || '';
			onChanged?.(value as T);
		}) as () => void;

		$scope.$on('$destroy', () => {
			unsubscribe();
		});

		return value as T;
	}

	private initScopeValue<T = unknown>(scopeValue: unknown, onChanged: (newValue: T) => void): T {
		const { $scope } = this.props;

		let value = scopeValue;

		if (typeof scopeValue === 'string') {
			const transformedScopeValue =
				scopeValue.startsWith('"') && scopeValue.endsWith('"') ? scopeValue : scopeValue.split('.').reduce((prev, current) => prev + `['${current}']`);

			value = $scope.$parent.$eval(transformedScopeValue);

			const unsubscribe = $scope.$parent.$watch(transformedScopeValue, (newValue) => {
				if (newValue !== value) {
					value = newValue;
					onChanged(value as T);
				}
			});

			$scope.$on('$destroy', () => {
				unsubscribe();
			});
		}

		return value as T;
	}

	private handleBooleanParams(params: IStrictParam[]): IStrictParam[] {
		params.forEach((param) => {
			if (param.Type === DataTypes.IntType) {
				if (param.Value === true) {
					param.Value = 1;
				} else if (param.Value === false || typeof param.Value === 'undefined') {
					param.Value = 0;
				}
			}
		});
		return params;
	}

	public toRequest(): FilterExpressionRequestParams {
		let properties: FilterExpressionRequestParams = {};
		if (this.filterDefinition?.IsStrict) {
			const params = this.handleBooleanParams(this.filterDefinition.StrictExpression?.Params || []);
			if (this.id) {
				properties = {
					FilterExpression: {
						Id: this.id,
						Params: params,
					},
				};
			} else if (this.path) {
				properties = {
					FilterExpression: {
						Path: this.path,
						Params: params,
					},
				};
			}
		} else if (this.expression) {
			properties = {
				WhereExpression: this.expression,
			};
			if (this.path) {
				properties.FilterExpression = {
					Path: this.path,
				};
			}
		}
		return properties;
	}

	public hasFilter(): boolean {
		return Boolean((this.filterDefinition?.IsStrict && (this.path || this.id)) || this.expression);
	}
}

export interface IStrictFilterDefinition {
	IsStrict: true;
	StrictExpression: IStrictExpression;
}

export interface ILegacyFilterDefinition {
	IsStrict?: false;
	LegacyExpression: string;
}

export type IFilterDefinition = IStrictFilterDefinition | ILegacyFilterDefinition;

export interface IStrictExpression {
	Params?: IStrictParam[];
}

export interface IStrictParam {
	Name: string;
	Value: unknown;
	Type: DataTypes;
}

export function initFilterExpression(props: FilterExpressionProps): FilterExpression {
	const { propertyName, controller } = props;
	const filterDefinitionAttrName = toCamelCase(propertyName);
	controller[filterDefinitionAttrName] = new FilterExpression(props);
	return controller[filterDefinitionAttrName];
}
